import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    LinearProgress,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as UncheckedIcon,
    Upload as UploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { MuiFileInput } from 'mui-file-input';
import {
    getWorkDetailAWS,
    getDocumentPresignedAWS,
    uploadFileAWS,
    insertDocumentAWS,
    deleteDocumentsAWS,
    getDocumentURLAWS
} from '../../offchain-logic/services';
import { updateWorkModern } from '../../services/authService';
import moment from 'moment';

interface Work {
    id_work: string;
    work_name: string;
    id_activity: string;
    id_model: string;
    work_date: string;
    description: string;
    status: string;
    completion_count: number;
    total_required: number;
    completion_percentage: number;
    creation_date: string;
    updated_date: string;
    document_checklist: Record<string, ChecklistSlot>;
}

interface ChecklistSlot {
    slot_name: string;
    order: number;
    is_required: boolean;
    id_document: string | null;
    file_name: string | null;
    upload_date: string | null;
}

interface Props {
    handleOpenToast: (message: string, severity: 'success' | 'error') => void;
}

export const WorkDetail: React.FC<Props> = ({ handleOpenToast }) => {
    const navigate = useNavigate();
    const { id_work } = useParams<{ id_work: string }>();
    
    const [work, setWork] = useState<Work | null>(null);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<{ slot_id: string; slot: ChecklistSlot } | null>(null);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadDescription, setUploadDescription] = useState('');
    
    // Verifica se l'utente è admin
    const userData = JSON.parse(sessionStorage.getItem('user') ?? "{}");
    const userRole = userData.role || '';
    const isAdmin = userRole === 'Admin';

    useEffect(() => {
        if (id_work) {
            loadWorkDetail();
        }
    }, [id_work]);

    const loadWorkDetail = () => {
        if (!id_work) return;
        
        getWorkDetailAWS(id_work, (err: any, data: any) => {
            if (err) {
                console.error('[WORK DETAIL] Error:', err);
                handleOpenToast('Errore caricamento dettaglio pratica', 'error');
                return;
            }
            
            if (data?.work) {
                setWork(data.work);
            }
        });
    };

    const handleUploadClick = (slot_id: string, slot: ChecklistSlot) => {
        setSelectedSlot({ slot_id, slot });
        setUploadFile(null);
        setUploadDescription(slot.slot_name); // Default description
        setOpenUploadDialog(true);
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile || !selectedSlot || !work) {
            handleOpenToast('Seleziona un file', 'error');
            return;
        }

        try {
            // Step 1: Get presigned URL
            await new Promise<void>((resolve, reject) => {
                getDocumentPresignedAWS(
                    uploadFile.name,
                    uploadFile.type,
                    work.id_activity,
                    work.id_work,
                    selectedSlot.slot_id,
                    (err: any, data: any) => {
                        if (err) {
                            reject(err);
                            return;
                        }

                        const { presigned_url, id_document, path } = data;

                        // Step 2: Upload to S3
                        uploadFileAWS(presigned_url, uploadFile, (uploadErr: any, uploadData: any) => {
                            if (uploadErr) {
                                reject(uploadErr);
                                return;
                            }

                            // Step 3: Insert metadata
                            const fileObject = {
                                id_document,
                                name: uploadFile.name,
                                description: uploadDescription,
                                id_activity: work.id_activity,
                                mime_type: uploadFile.type,
                                path,
                                id_work: work.id_work,
                                slot_id: selectedSlot.slot_id,
                                slot_name: selectedSlot.slot.slot_name,
                                slot_order: selectedSlot.slot.order
                            };

                            insertDocumentAWS(fileObject, (insertErr: any, insertData: any) => {
                                if (insertErr) {
                                    reject(insertErr);
                                    return;
                                }
                                resolve();
                            });
                        });
                    }
                );
            });

            setOpenUploadDialog(false);
            handleOpenToast('Documento caricato con successo', 'success');
            loadWorkDetail(); // Reload to update checklist

        } catch (error) {
            console.error('[UPLOAD] Error:', error);
            handleOpenToast('Errore durante il caricamento', 'error');
        }
    };

    const handleDeleteDocument = (slot_id: string, id_document: string, file_name: string) => {
        if (!window.confirm(`Vuoi eliminare il documento "${file_name}"?`)) {
            return;
        }

        deleteDocumentsAWS(id_document, (err: any, data: any) => {
            if (err) {
                console.error('[DELETE] Error:', err);
                handleOpenToast('Errore eliminazione documento', 'error');
                return;
            }

            handleOpenToast('Documento eliminato con successo', 'success');
            loadWorkDetail();
        });
    };

    const handleDownloadDocument = (id_document: string, file_name: string) => {
        getDocumentURLAWS(id_document, (err: any, data: any) => {
            if (err) {
                console.error('[DOWNLOAD] Error:', err);
                handleOpenToast('Errore download documento', 'error');
                return;
            }

            if (data?.presigned_url) {
                window.open(data.presigned_url, '_blank');
            }
        });
    };

    const handleCompleteUploads = async () => {
        if (!work || !id_work) return;
        
        try {
            await updateWorkModern(id_work, { status: 'pending' });
            handleOpenToast('Pratica inviata in attesa di verifica', 'success');
            loadWorkDetail(); // Ricarica per aggiornare lo stato
        } catch (error) {
            console.error('[COMPLETE] Error:', error);
            handleOpenToast('Errore invio pratica', 'error');
        }
    };

    const handleApproveWork = async () => {
        if (!work || !id_work) return;
        
        try {
            await updateWorkModern(id_work, { status: 'completed' });
            handleOpenToast('Pratica approvata e completata', 'success');
            loadWorkDetail(); // Ricarica per aggiornare lo stato
        } catch (error) {
            console.error('[APPROVE] Error:', error);
            handleOpenToast('Errore approvazione pratica', 'error');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'primary';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return 'Completato';
            case 'in_progress': return 'In Corso';
            case 'pending': return 'In Attesa';
            case 'cancelled': return 'Annullato';
            default: return status;
        }
    };

    if (!work) {
        return <Box sx={{ p: 3 }}>Caricamento...</Box>;
    }

    // Converti checklist in array ordinato
    const checklistArray = Object.entries(work.document_checklist)
        .map(([slot_id, slot]) => ({ slot_id, ...slot }))
        .sort((a, b) => a.order - b.order);

    const requiredSlots = checklistArray.filter(s => s.is_required);
    const optionalSlots = checklistArray.filter(s => !s.is_required);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/works')}
                    sx={{ mb: 2 }}
                >
                    Torna alla Lista
                </Button>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                            {work.work_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Creato: {moment(work.creation_date).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                    </Box>
                    <Chip 
                        label={getStatusLabel(work.status)}
                        color={getStatusColor(work.status)}
                        size="medium"
                    />
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Info Card */}
                <Grid item xs={12} md={5}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Informazioni Pratica
                            </Typography>
                            
                            {work.description && (
                                <>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Descrizione
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {work.description}
                                    </Typography>
                                </>
                            )}

                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Data Pratica
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {moment(work.work_date, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601]).format('DD/MM/YYYY')}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" gutterBottom>
                                Completamento
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Documenti obbligatori</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {work.completion_count}/{work.total_required}
                                </Typography>
                            </Box>
                            
                            <LinearProgress 
                                variant="determinate" 
                                value={work.completion_percentage}
                                sx={{ 
                                    height: 10, 
                                    borderRadius: 5,
                                    mb: 1,
                                    bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: work.completion_percentage === 100 ? '#4caf50' : '#1976d2'
                                    }
                                }}
                            />
                            
                            <Typography variant="h5" align="center" fontWeight="bold" color="primary">
                                {work.completion_percentage}%
                            </Typography>

                            {work.completion_percentage === 100 && (
                                <>
                                    <Alert severity="success" sx={{ mt: 2 }}>
                                        Tutti i documenti obbligatori sono stati caricati!
                                    </Alert>
                                    
                                    {work.status === 'in_progress' && (
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            sx={{ mt: 2 }}
                                            onClick={handleCompleteUploads}
                                        >
                                            Caricamento completato - Invia per verifica
                                        </Button>
                                    )}
                                </>
                            )}
                            
                            {/* Pulsante Admin per approvare pratica in pending con documenti al 100% */}
                            {isAdmin && work.status === 'pending' && work.completion_percentage === 100 && (
                                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Pratica in attesa di verifica
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        onClick={handleApproveWork}
                                    >
                                        Approva e Completa Pratica
                                    </Button>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Checklist Card */}
                <Grid item xs={12} md={7}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Checklist Documenti
                            </Typography>

                            {/* Required Documents */}
                            {requiredSlots.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" color="error" sx={{ mt: 2, mb: 1 }}>
                                        Documenti Obbligatori *
                                    </Typography>
                                    <List>
                                        {requiredSlots.map(({ slot_id, slot_name, id_document, file_name, upload_date }) => (
                                            <ListItem
                                                key={slot_id}
                                                sx={{
                                                    bgcolor: id_document ? '#f1f8e9' : '#fff',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: id_document ? '#c5e1a5' : '#e0e0e0'
                                                }}
                                            >
                                                <ListItemIcon>
                                                    {id_document ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <UncheckedIcon color="disabled" />
                                                    )}
                                                </ListItemIcon>
                                                
                                                <ListItemText
                                                    primary={slot_name}
                                                    secondary={
                                                        id_document && file_name
                                                            ? `${file_name} - Caricato il ${moment(upload_date).format('DD/MM/YYYY')}`
                                                            : 'Non caricato'
                                                    }
                                                />

                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {id_document ? (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownloadDocument(id_document, file_name!)}
                                                                color="primary"
                                                                title="Apri documento"
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>
                                                            {(isAdmin || (work.status !== 'completed' && work.status !== 'pending')) && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteDocument(slot_id, id_document, file_name!)}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            )}
                                                        </>
                                                    ) : (
                                                        (work.status === 'completed' || work.status === 'pending') ? null : (
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<UploadIcon />}
                                                                onClick={() => handleUploadClick(slot_id, work.document_checklist[slot_id])}
                                                            >
                                                                Carica
                                                            </Button>
                                                        )
                                                    )}
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}

                            {/* Optional Documents */}
                            {optionalSlots.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
                                        Documenti Opzionali
                                    </Typography>
                                    <List>
                                        {optionalSlots.map(({ slot_id, slot_name, id_document, file_name, upload_date }) => (
                                            <ListItem
                                                key={slot_id}
                                                sx={{
                                                    bgcolor: id_document ? '#e3f2fd' : '#fff',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: id_document ? '#90caf9' : '#e0e0e0'
                                                }}
                                            >
                                                <ListItemIcon>
                                                    {id_document ? (
                                                        <CheckCircleIcon color="primary" />
                                                    ) : (
                                                        <UncheckedIcon color="disabled" />
                                                    )}
                                                </ListItemIcon>
                                                
                                                <ListItemText
                                                    primary={slot_name}
                                                    secondary={
                                                        id_document && file_name
                                                            ? `${file_name} - Caricato il ${moment(upload_date).format('DD/MM/YYYY')}`
                                                            : 'Non caricato'
                                                    }
                                                />

                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {id_document ? (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownloadDocument(id_document, file_name!)}
                                                                color="primary"
                                                                title="Apri documento"
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>
                                                            {(isAdmin || (work.status !== 'completed' && work.status !== 'pending')) && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteDocument(slot_id, id_document, file_name!)}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            )}
                                                        </>
                                                    ) : (
                                                        (work.status === 'completed' || work.status === 'pending') ? null : (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<UploadIcon />}
                                                                onClick={() => handleUploadClick(slot_id, work.document_checklist[slot_id])}
                                                            >
                                                                Carica
                                                            </Button>
                                                        )
                                                    )}
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}

                            {checklistArray.length === 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Questa pratica non ha documenti associati
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Upload Dialog */}
            <Dialog 
                open={openUploadDialog} 
                onClose={() => setOpenUploadDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon />
                        Carica Documento: {selectedSlot?.slot.slot_name}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <MuiFileInput
                            value={uploadFile}
                            onChange={setUploadFile}
                            placeholder="Seleziona file"
                            fullWidth
                            sx={{ mb: 3 }}
                        />

                        <TextField
                            label="Descrizione"
                            value={uploadDescription}
                            onChange={(e) => setUploadDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            helperText="Descrizione del documento (opzionale)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUploadDialog(false)}>
                        Annulla
                    </Button>
                    <Button 
                        onClick={handleUploadSubmit}
                        variant="contained"
                        startIcon={<UploadIcon />}
                        disabled={!uploadFile}
                    >
                        Carica
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
